<template>
    <kiosk-team-term-details :teamId="props.teamId" :term="props.term">
        <div class="col-span-12 lg:col-span-10 flex flex-col gap-6 w-full max-w-(--breakpoint-sm)">
            <vx-alert v-if="statusExplanation" color="info" shadow :closeable="false" data-test="status-alert">
                These Terms are <strong>{{ termStatusLabels[props.term.status] }}</strong
                >.
                {{ statusExplanation }}
            </vx-alert>

            <term-form-title v-model="data" :canEdit="canEdit" />
            <term-form-description v-model="data" :canEdit="canEdit" />
            <term-form-content v-model="data" :canEdit="canEdit" />
            <term-form-effective-at v-model="data" :canEdit="canEdit" />
            <term-form-org-unit-coverage v-model="data" :teamId="props.teamId" :canEdit="canEdit" />
            <term-form-consent v-model="data" :canEdit="canEdit" />

            <div v-if="canPublish" class="sticky z-10 bottom-30">
                <vx-alert shadow :closeable="false" color="info">
                    These Terms are currently in draft. Once published, they can no longer be edited and will be
                    effective based on the date set above.
                    <template v-slot:right>
                        <vx-button
                            @click="confirmPublish()"
                            data-test="publish-button"
                            size="sm"
                            color="info"
                            :loading="preparingPublish"
                        >
                            Publish
                        </vx-button>
                    </template>
                </vx-alert>
            </div>

            <vx-sticky-footer>
                <vx-button :href="backHref" class="w-20" size="lg" color="muted">Cancel</vx-button>

                <vx-popover class="grow" hover arrow placement="top">
                    <vx-button
                        @click="submit()"
                        data-test="submit-button"
                        block
                        size="lg"
                        color="primary"
                        :loading="processing"
                        :disabled="!canEdit"
                    >
                        Update Terms
                    </vx-button>
                    <template v-if="!canEdit" v-slot:content>
                        <p class="max-w-xs text-sm text-center">
                            Only Draft Terms can be edited. Please create new Terms if you would like to make changes.
                        </p>
                    </template>
                </vx-popover>
            </vx-sticky-footer>
        </div>
    </kiosk-team-term-details>
</template>

<script lang="ts" setup>
import { VxAlert, VxPopover, VxButton, VxStickyFooter, VxSwal } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import KioskTeamTermDetails from './KioskTeamTermDetails.vue';
import TermFormTitle from './TermFormTitle.vue';
import TermFormDescription from './TermFormDescription.vue';
import TermFormContent from './TermFormContent.vue';
import TermFormEffectiveAt from './TermFormEffectiveAt.vue';
import TermFormOrgUnitCoverage from './TermFormOrgUnitCoverage.vue';
import TermFormConsent from './TermFormConsent.vue';
import { useTermFormSubmission } from './useTermFormSubmission';
import { termStatusExplanations, termStatusLabels } from './termStatus';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { escapeHtml } from '~/utils/html';
import { useToasted } from '~/composables/useToasted';
import { adminTermsClient } from '~/services';
import type { Term } from '~/services/adminTermsClient.types';

const props = defineProps<{
    teamId: number;
    term: Term;
}>();

const { data, submit, processing, canEdit, canPublish, publish, backHref, loadActiveTerm } = useTermFormSubmission(
    props.teamId,
    props.term
);

const statusExplanation = computed(() =>
    props.term.status === 'draft' ? null : termStatusExplanations[props.term.status]
);

const toasted = useToasted();
const preparingPublish = ref(false);

const coveredOrgUnits = (term: Term) => (term.org_unit_coverage === 'selected' ? term.org_units ?? [] : []);

const droppedCoverageWarning = (activeTerm: Term | null, draft: Term): string | null => {
    if (!activeTerm || activeTerm.org_unit_coverage === 'none' || draft.org_unit_coverage === 'all') {
        return null;
    }

    if (activeTerm.org_unit_coverage === 'all') {
        return 'OUs left out of these Terms stop accepting for their groups.';
    }

    const keptIds = new Set(coveredOrgUnits(draft).map((orgUnit) => orgUnit.id));
    const dropped = coveredOrgUnits(activeTerm).filter((orgUnit) => !keptIds.has(orgUnit.id));

    if (dropped.length === 0) {
        return null;
    }

    return `${dropped.map((orgUnit) => escapeHtml(orgUnit.name)).join(', ')} will no longer accept for their groups.`;
};

const publishWarning = async (): Promise<{ warning: string | null } | null> => {
    try {
        const [activeTerm, { data: draft }] = await Promise.all([
            loadActiveTerm(),
            adminTermsClient.getTerm(props.teamId, props.term.id),
        ]);

        return { warning: droppedCoverageWarning(activeTerm, draft) };
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));

        return null;
    }
};

const confirmPublish = async () => {
    if (preparingPublish.value) {
        return;
    }

    preparingPublish.value = true;
    const check = await publishWarning();
    preparingPublish.value = false;

    if (!check) {
        return;
    }

    const { warning } = check;

    VxSwal.fire({
        title: 'Publish Terms',
        html: `<div class="text-left">
            Publishing makes these Terms Active based on the effective date and locks them from edits. Here is what will happen:
            <ul class="list-disc pl-5 mt-2">
                <li>Any previously Active or Paused Terms will be archived.</li>
                <li>All owners will need to accept the new Terms.</li>
                ${
                    warning
                        ? `<li data-test="publish-dropped-coverage">${warning} Their groups must accept through their own owner, and groups without an owner will have no one who can accept.</li>`
                        : ''
                }
            </ul>
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'primary',
        preConfirm: () => publish().catch(throwSwalRequestError),
    });
};
</script>
